import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { OAuthConfig } from "../../services/orgAuthService";
import {
    cancelMemberInvitation,
    changeOwnPassword,
    fetchOrgMembers,
    fixMemberInvite,
    inviteMember,
    patchMember,
    regenerateMemberClaim,
    resetMemberPassword,
    signOutAllSessions
} from "../../services/orgMembers";
import type {
    ChangePasswordInput,
    ClaimLinkResponse,
    FixInviteInput,
    InviteMemberInput,
    InviteMemberResponse,
    OrgMember,
    OrgMemberListItem,
    PatchMemberInput,
    SuccessResponse
} from "../../types/orgMembers";

import { invalidateQueriesAfterMutation } from "./invalidateQueriesAfterMutation";

type OrgMembersConfig = Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">;

/**
 * Exported because the roster is invalidated from outside this file: a
 * permission change alters `permissions` on every list item, and the console
 * that makes that change has to be able to say so.
 */
export const ORG_MEMBERS_QUERY_KEY = ["org-members"] as const;

// ── List ─────────────────────────────────────────────────────────────

export interface UseOrgMembersReturn {
    members: OrgMemberListItem[];
    total: number;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<unknown>;
}

export function useOrgMembers(config: OrgMembersConfig): UseOrgMembersReturn {
    const { data, isPending, error, refetch } = useQuery({
        queryKey: ORG_MEMBERS_QUERY_KEY,
        queryFn: () => fetchOrgMembers(config),
        staleTime: 5 * 60 * 1000
    });

    return {
        members: data?.data ?? [],
        total: data?.total ?? 0,
        // Loading means "no roster yet", including a retry React Query has
        // paused (an unfocused tab or offline). `isLoading` drops to false on
        // that pause with no data and no error, and pages then rendered an
        // empty team instead of their loading state.
        isLoading: isPending,
        error,
        refetch
    };
}

// ── Invite ───────────────────────────────────────────────────────────

export interface UseInviteMemberReturn {
    inviteMember: (input: InviteMemberInput) => void;
    inviteMemberAsync: (input: InviteMemberInput) => Promise<InviteMemberResponse>;
    isInviting: boolean;
    error: Error | null;
}

export function useInviteMember(config: OrgMembersConfig): UseInviteMemberReturn {
    const queryClient = useQueryClient();
    const mutation = useMutation<InviteMemberResponse, Error, InviteMemberInput>({
        mutationFn: (input) => inviteMember(config, input),
        onSuccess: () => {
            invalidateQueriesAfterMutation(queryClient, [ORG_MEMBERS_QUERY_KEY]);
        }
    });

    return {
        inviteMember: (input) => mutation.mutate(input),
        inviteMemberAsync: (input) => mutation.mutateAsync(input),
        isInviting: mutation.isPending,
        error: mutation.error
    };
}

// ── Reset password ───────────────────────────────────────────────────

export interface UseResetMemberPasswordReturn {
    resetPassword: (uuid: string) => void;
    resetPasswordAsync: (uuid: string) => Promise<ClaimLinkResponse>;
    isResetting: boolean;
    error: Error | null;
}

export function useResetMemberPassword(config: OrgMembersConfig): UseResetMemberPasswordReturn {
    const mutation = useMutation<ClaimLinkResponse, Error, string>({
        mutationFn: (uuid) => resetMemberPassword(config, uuid)
    });

    return {
        resetPassword: (uuid) => mutation.mutate(uuid),
        resetPasswordAsync: (uuid) => mutation.mutateAsync(uuid),
        isResetting: mutation.isPending,
        error: mutation.error
    };
}

// ── Regenerate claim ─────────────────────────────────────────────────

export interface UseRegenerateMemberClaimReturn {
    regenerateClaim: (uuid: string) => void;
    regenerateClaimAsync: (uuid: string) => Promise<ClaimLinkResponse>;
    isRegenerating: boolean;
    error: Error | null;
}

export function useRegenerateMemberClaim(config: OrgMembersConfig): UseRegenerateMemberClaimReturn {
    const queryClient = useQueryClient();
    const mutation = useMutation<ClaimLinkResponse, Error, string>({
        mutationFn: (uuid) => regenerateMemberClaim(config, uuid),
        onSuccess: () => {
            invalidateQueriesAfterMutation(queryClient, [ORG_MEMBERS_QUERY_KEY]);
        }
    });

    return {
        regenerateClaim: (uuid) => mutation.mutate(uuid),
        regenerateClaimAsync: (uuid) => mutation.mutateAsync(uuid),
        isRegenerating: mutation.isPending,
        error: mutation.error
    };
}

// ── Fix invite ───────────────────────────────────────────────────────

export interface UseFixMemberInviteReturn {
    fixInvite: (input: { uuid: string; data: FixInviteInput }) => void;
    fixInviteAsync: (input: { uuid: string; data: FixInviteInput }) => Promise<InviteMemberResponse>;
    isFixing: boolean;
    error: Error | null;
}

export function useFixMemberInvite(config: OrgMembersConfig): UseFixMemberInviteReturn {
    const queryClient = useQueryClient();
    const mutation = useMutation<InviteMemberResponse, Error, { uuid: string; data: FixInviteInput }>({
        mutationFn: ({ uuid, data }) => fixMemberInvite(config, uuid, data),
        onSuccess: () => {
            invalidateQueriesAfterMutation(queryClient, [ORG_MEMBERS_QUERY_KEY]);
        }
    });

    return {
        fixInvite: (input) => mutation.mutate(input),
        fixInviteAsync: (input) => mutation.mutateAsync(input),
        isFixing: mutation.isPending,
        error: mutation.error
    };
}

// ── Cancel invitation ─────────────────────────────────────────────────

export interface UseCancelMemberInvitationReturn {
    cancelInvitation: (uuid: string) => void;
    cancelInvitationAsync: (uuid: string) => Promise<void>;
    isCancelling: boolean;
    error: Error | null;
}

export function useCancelMemberInvitation(config: OrgMembersConfig): UseCancelMemberInvitationReturn {
    const queryClient = useQueryClient();
    const mutation = useMutation<void, Error, string>({
        mutationFn: (uuid) => cancelMemberInvitation(config, uuid),
        onSuccess: () => {
            invalidateQueriesAfterMutation(queryClient, [ORG_MEMBERS_QUERY_KEY]);
        }
    });

    return {
        cancelInvitation: (uuid) => mutation.mutate(uuid),
        cancelInvitationAsync: (uuid) => mutation.mutateAsync(uuid),
        isCancelling: mutation.isPending,
        error: mutation.error
    };
}

// ── Patch member ─────────────────────────────────────────────────────

export interface UsePatchMemberReturn {
    patchMember: (input: { uuid: string; data: PatchMemberInput }) => void;
    patchMemberAsync: (input: { uuid: string; data: PatchMemberInput }) => Promise<OrgMember>;
    isPatching: boolean;
    error: Error | null;
}

export function usePatchMember(config: OrgMembersConfig): UsePatchMemberReturn {
    const queryClient = useQueryClient();
    const mutation = useMutation<OrgMember, Error, { uuid: string; data: PatchMemberInput }>({
        mutationFn: ({ uuid, data }) => patchMember(config, uuid, data),
        onSuccess: () => {
            invalidateQueriesAfterMutation(queryClient, [ORG_MEMBERS_QUERY_KEY]);
        }
    });

    return {
        patchMember: (input) => mutation.mutate(input),
        patchMemberAsync: (input) => mutation.mutateAsync(input),
        isPatching: mutation.isPending,
        error: mutation.error
    };
}

// ── Change own password ──────────────────────────────────────────────

export interface UseChangeOwnPasswordReturn {
    changePassword: (input: ChangePasswordInput) => void;
    changePasswordAsync: (input: ChangePasswordInput) => Promise<SuccessResponse>;
    isChanging: boolean;
    error: Error | null;
}

export function useChangeOwnPassword(config: OrgMembersConfig): UseChangeOwnPasswordReturn {
    const mutation = useMutation<SuccessResponse, Error, ChangePasswordInput>({
        mutationFn: (input) => changeOwnPassword(config, input)
    });

    return {
        changePassword: (input) => mutation.mutate(input),
        changePasswordAsync: (input) => mutation.mutateAsync(input),
        isChanging: mutation.isPending,
        error: mutation.error
    };
}

// ── Sign out all sessions ────────────────────────────────────────────

export interface UseSignOutAllSessionsReturn {
    signOutAll: () => void;
    signOutAllAsync: () => Promise<SuccessResponse>;
    isSigningOut: boolean;
    error: Error | null;
}

export function useSignOutAllSessions(config: OrgMembersConfig): UseSignOutAllSessionsReturn {
    const mutation = useMutation<SuccessResponse, Error, void>({
        mutationFn: () => signOutAllSessions(config)
    });

    return {
        signOutAll: () => mutation.mutate(),
        signOutAllAsync: () => mutation.mutateAsync(),
        isSigningOut: mutation.isPending,
        error: mutation.error
    };
}
